/**
 * React Flow edge interactions that mirror state into Loro: new connections,
 * bulk/single delete, and style updates (including persisting merged edge data).
 *
 * Expects `lastEdgeStyle` from `useCanvasShapeAndEdgeStyle` so new edges pick up
 * the current toolbar style. All handlers no-op when the user lacks `can_edit`
 * (see `useCanvasPermissions`).
 */

import {
  DEFAULT_EDGE_STYLE,
  type LastEdgeStyle,
} from "@/pages/canvas/hooks/useCanvasShapeAndEdgeStyle";
import type { EdgeStyle } from "@/pages/canvas/nodes/EdgeFloatingToolbar";
import {
  applyEdgeStyle,
  readEdgeStyle,
} from "@/pages/canvas/utils/applyEdgeStyle";
import type { Connection, Edge, Node } from "@xyflow/react";
import { useReactFlow } from "@xyflow/react";
import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { useCanvas } from "../context/CanvasContext";
import { useCanvasPermissions } from "./useCanvasPermissions";

/** Matches ahooks `useSessionStorageState` setter (`createUseStorageState.SetState`). */
type AhooksStorageSetter<T> = (value?: T | ((prevState?: T) => T)) => void;

export interface UseCanvasEdgeHandlersParams {
  setLastEdgeStyle: AhooksStorageSetter<LastEdgeStyle>;
  lastEdgeStyle: LastEdgeStyle;
}

export function useCanvasEdgeHandlers({
  setLastEdgeStyle,
  lastEdgeStyle,
}: UseCanvasEdgeHandlersParams) {
  const { setEdges } = useReactFlow<Node, Edge>();
  const { canEdit } = useCanvasPermissions();
  const { addEdge, removeEdge, removeEdges } = useCanvas();
  const handleConnect = useCallback(
    (connection: Connection) => {
      if (!canEdit) return;
      // Translate the chosen style into native RF fields; `data` keeps only
      // curve_type. Native fields are what renders + persists.
      const styled = applyEdgeStyle({
        ...connection,
        id: uuidv4(),
        data: lastEdgeStyle as Record<string, unknown>,
      });
      const edge: Edge = {
        ...styled,
        data: { curve_type: lastEdgeStyle.curve_type },
      };
      setEdges((eds) => [...eds, edge]);
      addEdge({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        type: edge.type,
        sourceHandle: edge.sourceHandle ?? undefined,
        targetHandle: edge.targetHandle ?? undefined,
        style: edge.style,
        markerStart: edge.markerStart,
        markerEnd: edge.markerEnd,
        data: edge.data as Record<string, unknown>,
      });
    },
    [canEdit, setEdges, addEdge, lastEdgeStyle],
  );

  const handleEdgesDelete = useCallback(
    (deletedEdges: Edge[]) => {
      if (!canEdit || deletedEdges.length === 0) return;
      removeEdges(deletedEdges.map((e) => e.id));
    },
    [canEdit, removeEdges],
  );

  const handleEdgeStyleChange = useCallback(
    (edgeId: string, style: EdgeStyle) => {
      if (!canEdit) return;
      setLastEdgeStyle((prev) => ({
        ...(prev ?? DEFAULT_EDGE_STYLE),
        ...style,
      }));
      setEdges((eds) => {
        return eds.map((e) => {
          if (e.id !== edgeId) return e;
          // Current semantic style comes from the edge's native fields; merge
          // the toolbar's change and re-apply to native fields.
          const merged: EdgeStyle = { ...readEdgeStyle(e), ...style };
          const styled = applyEdgeStyle({
            ...e,
            data: merged as Record<string, unknown>,
          });
          const edge: Edge = {
            ...styled,
            data: { curve_type: merged.curve_type },
          };
          addEdge({
            id: edgeId,
            source: edge.source,
            target: edge.target,
            type: edge.type,
            sourceHandle: edge.sourceHandle ?? undefined,
            targetHandle: edge.targetHandle ?? undefined,
            style: edge.style,
            markerStart: edge.markerStart,
            markerEnd: edge.markerEnd,
            data: edge.data as Record<string, unknown>,
            // `add` is a full edge replace in Liveblocks storage (LiveObject
            // reconcile in "full" mode deletes keys absent from the payload),
            // so carry the existing label through — `e` is the original edge
            // (applyEdgeStyle's `edge` doesn't include `label`). Without this,
            // a style change would wipe a label set earlier.
            label: e.label,
          });
          return edge;
        });
      });
    },
    [canEdit, setEdges, addEdge, setLastEdgeStyle],
  );

  const handleEdgeDelete = useCallback(
    (edgeId: string) => {
      if (!canEdit) return;
      removeEdge(edgeId);
      setEdges((eds) => eds.filter((e) => e.id !== edgeId));
    },
    [canEdit, removeEdge, setEdges],
  );

  return {
    handleConnect,
    handleEdgesDelete,
    handleEdgeStyleChange,
    handleEdgeDelete,
  };
}
